import { Button, Typography } from '@mui/material';
import UploadIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';
import React, { FC, useState } from 'react';
import { FILE_TYPES, vImageTypes } from '../Form/form.validations';
import { formatBytes } from '../../lib/helpers/generalFormatters';
import { FormFieldValidation } from '../../lib/interfaces/Form';
import './FileUploader.scss';

type FileUploadProps = {
	validation?: FormFieldValidation;
	label: string;
	required: boolean;
	defaultValue: string | undefined;
	// eslint-disable-next-line no-unused-vars
	onFileUploaded: (value: File | undefined) => void;
};

const FileUpload: FC<FileUploadProps> = ({
	label,
	required,
	defaultValue,
	onFileUploaded,
	validation,
}) => {
	const { t } = useTranslation();
	const [image, setImage] = useState<string>(defaultValue || '');
	const [file, setFile] = useState<File | null>();
	const [validationError, setValidationError] = useState<string>('');
	const inputFileRef = React.useRef<HTMLInputElement>(null);

	const onUploadBtnClick = () => {
		if (inputFileRef) {
			inputFileRef?.current?.click();
		}
	};

	const validateFile = (file: File) => {
		if (validation) {
			if (validation.maxFileByteSize && file.size > validation.maxFileByteSize) {
				const errorMessage = t('maxSizeAvatarError', {
					actualSize: formatBytes(file.size),
					maxSize: formatBytes(validation.maxFileByteSize),
				});
				setValidationError(errorMessage);
				return;
			}
			if (
				validation.allowedExtensions &&
				!vImageTypes(
					file,
					validation.allowedExtensions.map((type) => type.value),
				)
			) {
				const allowedExtentions = validation.allowedExtensions
					.map((type) => type.label)
					.join(', ');
				setValidationError(t('imageTypeError', { allowedExtentions }));
				return;
			}
			setValidationError('');
		}
		return true;
	};

	const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event?.target?.files?.[0];
		if (file && validateFile(file)) {
			setFile(file);
			if (file) {
				const [type] = file.type.split('/');
				if (type === 'image') {
					setImage(URL.createObjectURL(file));
				} else if (file.type === FILE_TYPES.PDF.value) {
					setImage(
						'https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg',
					);
				} else if (
					file.type === FILE_TYPES.DOC.value ||
					file.type === FILE_TYPES.DOCX.value
				) {
					setImage(
						'https://upload.wikimedia.org/wikipedia/commons/f/fb/.docx_icon.svg',
					);
				}
				onFileUploaded(file);
			}
		}
	};
	const onRemoveFile = () => {
		setImage('');
		onFileUploaded(undefined);
		setFile(null);
	};

	return (
		<div className="FileUpload">
			{image && <img className="FileUpload__thumbnail" src={image} alt="file" />}
			<Typography variant="body1" component="div">
				{file?.name}
			</Typography>
			<div>
				<div>
					<input
						ref={inputFileRef}
						required={required}
						type="file"
						id="image"
						name="image"
						accept=".doc,.docx, image/png, image/jpeg, application/pdf"
						onChange={onChange}
						style={{ display: 'none' }}
					/>
				</div>
				<div onClick={(e) => e.stopPropagation()}>
					<Button
						onClick={onUploadBtnClick}
						startIcon={<UploadIcon />}
						variant="outlined"
					>
						{t('upload')} {label}
					</Button>
					{file && (
						<Button
							sx={{ marginLeft: '10px' }}
							onClick={onRemoveFile}
							startIcon={<UploadIcon />}
							variant="outlined"
						>
							{t('removeFile')}
						</Button>
					)}
				</div>
				{validationError && (
					<div className="FileUpload__error">{validationError}</div>
				)}
			</div>
		</div>
	);
};

export default FileUpload;
